import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import '../features/persons/pessoas-2b.css';
import {
  ConversasSection,
  PendenciasSection,
  AppointmentsSection,
  WaitlistSection,
} from '../features/persons/PersonActivitySections';
import {
  IdentitySection,
  IdentidadesSection,
  DadosColetadosSection,
  BondsSection,
} from '../features/persons/PersonCoreSections';
import { PersonHeader, buildPersonTabs } from '../features/persons/PersonHeader';
import {
  SummariesSection,
  BubblesSection,
  AdversarialMediationAlert,
} from '../features/persons/PersonRegulatedSections';
import { bondIsAdversarial, personInitials, maskCpf } from '../features/persons/persons-adapter';
import { PortfolioSection } from '../features/persons/PortfolioEditor';
import { usePerson } from '../features/persons/use-persons';
import { useCapability } from '../hooks/use-capability';

/**
 * PessoaDetailPage — rota `/pessoas/:personId` (Brief 2B T2).
 *
 * Ficha 360º consolidada da pessoa (P18 multi-cardinal) num único GET. Seções
 * com nav sticky scroll-to. Read-only. Bubbles só aparecem quando o backend as
 * retorna (gated por `confidentiality-bubbles-full`). Appointments gated por
 * cap `appointments`.
 */
export function PessoaDetailPage(): JSX.Element {
  const { personId } = useParams<{ personId: string }>();
  const navigate = useNavigate();
  const hasAppointmentsCap = useCapability('appointments');
  // Frente B Sessão 3 — fila de espera na ficha 360º só p/ tenant com fila.
  const hasWaitlistCap = useCapability('scheduling-waitlist');
  // S4 PR-b (Corrente de Compromissos, Item 404 faceta a) — carteira (Item 377)
  // só aparece na ficha p/ quem tem a cap de leitura (molde hasWaitlistCap).
  const hasPortfolioCap = useCapability('customer-portfolio-read');
  const { data: person, isLoading, isError } = usePerson(personId);

  const [activeTab, setActiveTab] = useState('identity');
  // Trava o scroll-spy durante o scroll programático do clique numa aba — senão
  // a seção seguinte entra na banda do observer e "rouba" o destaque (a aba à
  // direita acende). Liberado após a animação de scroll assentar.
  const spyLockedUntil = useRef(0);

  const scrollTo = (id: string): void => {
    setActiveTab(id);
    spyLockedUntil.current = Date.now() + 800;
    const el = document.querySelector(`[data-section="${id}"]`);
    if (el !== null) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Scroll-spy: marca a aba ativa = a seção MAIS AO TOPO da banda logo abaixo do
  // cabeçalho sticky. Mantém um mapa persistente de visibilidade (o callback só
  // recebe as entries que mudaram) e escolhe a de menor `top` — não a de maior
  // ratio (que elegia a vizinha de baixo quando havia pouco conteúdo).
  useEffect(() => {
    if (person === undefined) return;
    const sections = Array.from(document.querySelectorAll<HTMLElement>('[data-section]'));
    if (sections.length === 0) return;
    const visibility = new Map<string, IntersectionObserverEntry>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const id = e.target.getAttribute('data-section');
          if (id !== null) visibility.set(id, e);
        }
        if (Date.now() < spyLockedUntil.current) return;
        const intersecting = [...visibility.values()]
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const id = intersecting[0]?.target.getAttribute('data-section');
        if (id !== null && id !== undefined) setActiveTab(id);
      },
      { rootMargin: '-120px 0px -60% 0px', threshold: 0 },
    );
    for (const s of sections) observer.observe(s);
    return () => {
      observer.disconnect();
    };
  }, [person]);

  // Frente C (spec 2026-08-12 §3.1) — deep-link `#<sectionId>` (ex.:
  // /pessoas/:id#conversas vindo do header da Conversa em curso). Roda quando a
  // pessoa carrega; reusa o scrollTo (trava o scroll-spy e destaca a aba).
  const location = useLocation();
  const personLoaded = person !== undefined;
  useEffect(() => {
    if (!personLoaded) return;
    const sectionId = location.hash.replace('#', '');
    if (sectionId === '') return;
    if (document.querySelector(`[data-section="${sectionId}"]`) === null) return;
    scrollTo(sectionId);
    // scrollTo é estável por render e intencionalmente fora das deps (setState+ref).
  }, [personLoaded, location.hash]);

  if (isLoading) {
    return <div className="person-list-page">Carregando pessoa…</div>;
  }
  if (isError || person === undefined) {
    return (
      <div className="person-list-page">
        <div className="person-empty">
          Não foi possível carregar a pessoa.{' '}
          <button className="link-btn" type="button" onClick={() => navigate('/pessoas')}>
            Voltar à busca
          </button>
        </div>
      </div>
    );
  }

  const hasBubbles = person.bubbles.length > 0;
  const hasAdversarialBond = person.bonds.some(bondIsAdversarial);
  const tabs = buildPersonTabs({
    hasAppointments: hasAppointmentsCap,
    hasBubbles,
    hasWaitlist: hasWaitlistCap,
    hasPortfolio: hasPortfolioCap,
  });

  const cpf =
    person.primaryIdentifierType === 'cpf' && person.primaryIdentifierValue !== null
      ? `CPF ${maskCpf(person.primaryIdentifierValue)}`
      : (person.primaryIdentifierValue ?? personInitials(person.displayName));

  return (
    <div className="person-page">
      <PersonHeader
        name={person.displayName}
        subtitle={cpf}
        tabs={tabs}
        activeTab={activeTab}
        onTab={scrollTo}
      />

      {hasAdversarialBond && <AdversarialMediationAlert />}

      <IdentitySection
        person={person}
        onStartConversation={() => navigate(`/iniciar-conversa?personId=${person.id}`)}
      />
      <IdentidadesSection person={person} />
      <DadosColetadosSection person={person} />
      <BondsSection person={person} />
      {hasPortfolioCap && <PortfolioSection personId={person.id} />}
      <ConversasSection personId={person.id} />
      <PendenciasSection pendencyCount={person.pendencyCount} />
      {hasAppointmentsCap && <AppointmentsSection personId={person.id} />}
      {hasWaitlistCap && <WaitlistSection personId={person.id} />}
      <SummariesSection />
      {hasBubbles && <BubblesSection bubbles={person.bubbles} />}
    </div>
  );
}
